import type { FC, ReactNode } from 'react';
import React, { createContext, useContext, useMemo } from 'react';

type OverlayNavClose = () => void;

interface OverlayNavCloseContextValue {
  readonly onClose: OverlayNavClose;
  readonly closeControlInHeader: boolean;
}

const OverlayNavCloseContext = createContext<OverlayNavCloseContextValue | undefined>(undefined);

interface OverlayNavCloseProviderProps {
  readonly onClose: OverlayNavClose;
  /**
   * When true, the navigation host renders its own close control (e.g. in the header) and the
   * default close control in the top bar is suppressed.
   */
  readonly closeControlInHeader?: boolean;
  readonly children: ReactNode;
}

/**
 * Provides a close handler to the navigation subtree when it is rendered inside a full-screen
 * overlay. When a handler is present, the nav renders a close control (in place of the collapse
 * toggle) that dismisses the overlay.
 */
export const OverlayNavCloseProvider: FC<OverlayNavCloseProviderProps> = ({
  onClose,
  closeControlInHeader = false,
  children,
}) => {
  const value = useMemo(() => ({ onClose, closeControlInHeader }), [onClose, closeControlInHeader]);

  return <OverlayNavCloseContext.Provider value={value}>{children}</OverlayNavCloseContext.Provider>;
};

export const useOverlayNavClose = (): OverlayNavClose | undefined => useContext(OverlayNavCloseContext)?.onClose;

export const useIsOverlayNavCloseInHeader = (): boolean =>
  useContext(OverlayNavCloseContext)?.closeControlInHeader ?? false;
